import { type FC } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faArrowLeft,
  faCog,
} from '@fortawesome/free-solid-svg-icons';
import PublicNavBar from '../../components/layout/PublicNavBar';
import PipelineStrip from '../../components/layout/PipelineStrip';
import PublicFooter from '../../components/layout/PublicFooter';
import ConsolePanel from './shared/ConsolePanel';
import WFAResultsHero from './validation/WFAResultsHero';
import BaselineResultsMock from './validation/BaselineResultsMock';
import WFADeepDiveCards from './validation/WFADeepDiveCards';
import AdaptiveFlowPreviewMock from './validation/AdaptiveFlowPreviewMock';
import DegradationDetectionMock from './validation/DegradationDetectionMock';
import { usePageMeta } from '../../hooks/usePageMeta';

/* ═══════════════════════════ PAGE ═════════════════════════════════════ */

const ValidationPage: FC = () => {
  usePageMeta({ title: 'Validation Pipeline', description: 'Structured validation for systematic strategies. Walk-forward analysis, baseline scans, and cross-asset testing to prove robustness before deployment.' });

  return (
    <div className="min-h-screen bg-[#0a0e14] text-gray-100 font-[Poppins] selection:bg-[#9d4edd]/30">
      <PublicNavBar />

      {/* ════════════════ 1. HERO ════════════════ */}
      <section className="relative pt-24 sm:pt-36 pb-28 px-4 sm:px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f1520] via-[#0a0e14] to-[#080b11]" />
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.15) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

        <div className="max-w-[1440px] mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
          {/* Left -- copy */}
          <div>
            <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/60 mb-5">
              02 &mdash; Validation Pipeline
            </p>

            <h1 className="text-3xl sm:text-[2.25rem] md:text-[2.5rem] font-bold leading-[1.1] tracking-[-0.025em] mb-4 text-white">
              Structured validation for systematic strategies
            </h1>

            <p className="text-[15px] text-gray-300/70 leading-[1.7] mb-3 max-w-[500px]">
              A disciplined pipeline that forces strategies to prove robustness
              across assets, parameters, and time &mdash; before they ever
              reach deployment.
            </p>

            <p className="text-[14px] text-white/60 italic mb-5">
              Validation isn&apos;t a checkbox. It&apos;s a gate.
            </p>

            <p className="text-[12px] font-mono uppercase tracking-widest text-gray-500 mb-2">Requirements</p>
            <div className="space-y-2 mb-5">
              {[
                'Must generalize across assets',
                'Must remain stable across parameter ranges',
                'Must survive rolling out-of-sample windows',
                'Must maintain behaviour as new data arrives',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-[#9d4edd]/50 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-400 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 mb-5">
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2.5 px-7 py-3 bg-[#9d4edd] hover:bg-[#8b3ec7] text-white rounded text-sm font-medium transition-colors"
              >
                Start Research
                <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
              </Link>
              <a href="#pipeline-overview" className="text-[13px] text-gray-400 hover:text-gray-200 transition-colors">
                View Pipeline Overview <span className="text-gray-600">&rarr;</span>
              </a>
            </div>

            <p className="text-[12px] text-gray-500 leading-snug max-w-[440px]">
              Outputs: pass/fail gates, stability scores, regime diagnostics, and a deployment readiness profile.
            </p>
          </div>

          {/* Right -- Hero visual */}
          <WFAResultsHero />
        </div>
      </section>

      <PipelineStrip active="validation" />

      {/* ════════════════ 2. PIPELINE OVERVIEW ════════════════ */}
      <section id="pipeline-overview" className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F9FAFB]">
        <div className="max-w-[960px] mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-xl md:text-[24px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              Five stages of structured validation
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] max-w-[560px] mx-auto">
              Every strategy in Quanthop moves through a defined validation
              pipeline. Each stage tests a different dimension of strategy
              robustness, and no stage can be skipped.
            </p>
          </div>

          {/* 5 stages with connecting line */}
          <div className="space-y-0">
            {[
              {
                num: '01',
                title: 'Baseline',
                subtitle: 'Multi-asset validation',
                text: 'Tests strategy logic across a basket of assets using default parameters. Must demonstrate generalization before any optimization occurs.',
                output: 'Generalization score + cross-asset profile',
                decision: 'Proceed to stability exploration / Stop',
              },
              {
                num: '02',
                title: 'Stability',
                subtitle: 'Parameter exploration',
                text: 'Explores parameter combinations and evaluates performance clustering. Identifies stable regions rather than single optimal points.',
                output: 'Cluster stability + robustness regions',
                decision: 'Proceed to WFA / Stop',
              },
              {
                num: '03',
                title: 'WFA',
                subtitle: 'Temporal consistency',
                text: 'Rolling in-sample optimization and out-of-sample testing windows. Tests whether parameter choices remain valid outside the fitting period.',
                output: 'OOS consistency + window breakdown',
                decision: 'Proceed to Adaptive Flow / Stop',
              },
              {
                num: '04',
                title: 'Adaptive Flow',
                subtitle: 'Rolling validation',
                text: 'Continuous rolling validation with structured re-optimization rules. Strategies are retested as new data arrives.',
                output: 'Re-optimization cycle results + trigger logs',
                decision: 'Ready for monitoring / Not ready',
              },
              {
                num: '05',
                title: 'Health',
                subtitle: 'Degradation detection',
                text: 'Tracks structural degradation, stability drift, and performance decay over time. Alerts when behaviour deviates from validated baselines.',
                output: 'Degradation alerts + health score',
                decision: 'Maintain / Re-validate / Retire',
              },
            ].map((stage, i) => (
              <div key={stage.num} className="relative">
                {i < 4 && (
                  <div className="absolute left-[15px] top-[48px] w-px h-[calc(100%-20px)] bg-gray-300/40" />
                )}
                <div className="flex items-start gap-5 py-5">
                  <div className="w-[30px] h-[30px] rounded-lg bg-[#9d4edd]/10 border border-[#9d4edd]/20 flex items-center justify-center flex-shrink-0 relative z-10">
                    <span className="font-mono text-[11px] text-[#9d4edd] font-semibold">{stage.num}</span>
                  </div>
                  <div className="pt-0.5">
                    <h3 className="text-[15px] font-semibold text-gray-950 mb-0.5">{stage.title} <span className="font-normal text-gray-500">({stage.subtitle})</span></h3>
                    <p className="text-[13px] text-gray-500 leading-relaxed mb-2">{stage.text}</p>
                    <p className="text-[12px] text-gray-400 font-mono mb-1">Output: <span className="italic">{stage.output}</span></p>
                    <p className="text-[11px] text-[#9d4edd]/70 font-mono">Gate: {stage.decision}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════ 3. BASELINE -- DEEP DIVE ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F0F2F5]">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Stage 01
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              Baseline multi-asset scan
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Before optimization, Quanthop requires a strategy to demonstrate
              cross-asset generalization using default parameters. If the core
              logic only works on one market, it stops here.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">What the baseline measures:</p>
            <div className="space-y-2 mb-6">
              {[
                'Cross-asset win rate and profit factor with default parameters',
                'Behaviour consistency across different market structures',
                'Structural edge detection (not curve-fitted performance)',
                'Pass/fail threshold based on minimum asset generalization',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-[14px] text-gray-900 leading-[1.7] font-medium mb-2">
              Strategies that only work on one asset are stopped here &mdash; before they waste research time.
            </p>
            <p className="text-[12px] text-[#9d4edd]/70 font-mono">
              Gate: Proceed to stability exploration / Stop
            </p>
          </div>

          {/* Mock of BaselineResultsGrid */}
          <BaselineResultsMock />
        </div>
      </section>

      {/* ════════════════ 4. PARAMETER STABILITY -- DEEP DIVE ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-white">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-12 lg:gap-18 items-center">
          <ConsolePanel
            title="Optimization"
            context="BTCUSDT 1h"
            icon={faCog}
            lines={[
              { time: '09:14:02', type: 'info', source: 'engine', message: 'Exploring 420 parameter combinations' },
              { time: '09:14:02', type: 'info', source: 'engine', message: 'fastLength: [5, 10, 15, 20, 25, 30, 35, 40, 45, 50]' },
              { time: '09:14:02', type: 'info', source: 'engine', message: 'slowLength: [10, 20, 30, 40, 50, 60, ..., 200]' },
              { time: '09:17:45', type: 'success', source: 'cluster', message: '3 stable parameter clusters identified' },
              { time: '09:17:45', type: 'info', source: 'cluster', message: 'Primary cluster: fastLength [8-14], slowLength [18-28]' },
              { time: '09:17:45', type: 'info', source: 'cluster', message: 'Cluster stability: 0.74' },
              { time: '09:17:46', type: 'success', source: 'result', message: 'No parameter degeneracy detected' },
              { time: '09:17:46', type: 'warning', source: 'result', message: 'Secondary cluster shows sensitivity to slowLength > 150' },
            ]}
          />

          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Stage 02
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-3">
              Parameter stability, not parameter optimality
            </h2>
            <p className="text-[15px] text-gray-800 leading-[1.7] mb-4 font-medium">
              A strategy is only credible if nearby parameters behave similarly.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-2">
              We score <em>regions</em> of parameters &mdash; not single &ldquo;best&rdquo; points.
              Cluster stability measures how consistently a parameter set performs
              across neighbouring values, not its absolute peak.
            </p>
            <p className="text-[13px] text-gray-500 leading-[1.7] mb-6">
              Example: EMA(18&ndash;24) behaves consistently &rarr; stable. EMA(21 only) wins &rarr; fragile.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">This guards against</p>
            <div className="space-y-2 mb-6">
              {[
                'Parameter overfitting to specific data windows',
                'Fragile parameter selections that fail under market changes',
                'Degeneracy where many different parameters produce similar results by chance',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-[14px] text-gray-900 leading-[1.7] font-medium mb-2">
              High cluster stability means performance is more likely to hold on unseen data.
            </p>
            <p className="text-[12px] text-[#9d4edd]/70 font-mono">
              Gate: Proceed to WFA / Stop
            </p>
          </div>
        </div>
      </section>

      {/* ════════════════ 5. WALK-FORWARD -- DEEP DIVE ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F9FAFB]">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Stage 03
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              Walk-forward analysis: testing temporal stability
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-2">
              Walk-forward analysis divides historical data into rolling
              in-sample (optimization) and out-of-sample (validation) windows.
              Parameters are optimized on each in-sample segment and immediately
              tested on the following out-of-sample period.
            </p>
            <p className="text-[15px] text-gray-800 leading-[1.7] mb-4 font-medium">
              A strategy passes only if OOS windows remain within defined performance and risk tolerances.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">What you learn:</p>
            <div className="space-y-2 mb-6">
              {[
                'Where the strategy works (regimes and time periods)',
                'Where it breaks (degradation windows)',
                'Whether it needs adaptive re-optimization',
                'OOS consistency ratio across all windows',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-[14px] text-gray-900 leading-[1.7] font-medium mb-2">
              A strategy that performs well in-sample but degrades
              out-of-sample is overfitted. Walk-forward exposes this.
            </p>
            <p className="text-[12px] text-[#9d4edd]/70 font-mono">
              Gate: Proceed to Adaptive Flow / Stop
            </p>
          </div>

          {/* Mock of WFAResultsGrid -- 4 cards */}
          <WFADeepDiveCards />
        </div>
      </section>

      {/* ════════════════ 6. ADAPTIVE FLOW -- DEEP DIVE ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F0F2F5]">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[7fr_5fr] gap-12 lg:gap-16 items-center">
          {/* Adaptive Flow mock */}
          <AdaptiveFlowPreviewMock />

          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Stage 04
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              Adaptive Flow: continuous rolling validation
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-2">
              Passing walk-forward is not enough. Markets evolve, and so must
              strategy validation.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-4">
              Adaptive Flow runs strategies in a rolling validation mode that
              accumulates new out-of-sample candles, tracks live trades against
              expected distributions, and triggers structured re-optimization
              cycles based on predefined rules.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">What Adaptive Flow monitors</p>
            <div className="space-y-2 mb-6">
              {[
                'Live vs expected return distribution',
                'Drawdown tolerance levels',
                'Candle accumulation towards re-optimization trigger',
                'Trade frequency against expectations',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-[14px] text-gray-900 leading-[1.7] font-medium mb-2">
              Strategies are never &ldquo;set and forget&rdquo; &mdash; they
              are continuously revalidated against live data.
            </p>
            <p className="text-[12px] text-[#9d4edd]/70 font-mono">
              Gate: Ready for monitoring / Not ready
            </p>
          </div>
        </div>
      </section>

      {/* ════════════════ 7. DEGRADATION DETECTION -- DEEP DIVE ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-white">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Stage 05
            </p>
            <h2 className="text-xl md:text-[22px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              Degradation detection
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-2">
              Validation does not end after deployment.
            </p>
            <p className="text-[15px] text-gray-600 leading-[1.7] mb-6">
              The health monitor continuously tracks strategy behaviour against
              validated baselines. When metrics drift beyond tolerance, structured
              alerts fire before performance degrades significantly.
            </p>

            <p className="text-[13px] text-gray-500 mb-3 font-medium">Monitored signals</p>
            <div className="space-y-2 mb-6">
              {[
                'Return distribution drift',
                'Drawdown behaviour drift',
                'Signal frequency drift',
                'Parameter region drift / stability decay',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="w-1 h-1 rounded-full bg-gray-400 mt-[7px] flex-shrink-0" />
                  <span className="text-[13px] text-gray-700 leading-snug">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-[14px] text-gray-900 leading-[1.7] font-medium mb-2">
              A healthy strategy is one whose behaviour remains consistent
              with its validated profile.
            </p>
            <p className="text-[12px] text-[#9d4edd]/70 font-mono">
              Gate: Maintain / Re-validate / Retire
            </p>
          </div>

          {/* Degradation Detection mock */}
          <DegradationDetectionMock />
        </div>
      </section>

      {/* ════════════════ 8. WHY STRUCTURED VALIDATION ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-[#F9FAFB]">
        <div className="max-w-[960px] mx-auto">
          <div className="text-center mb-14">
            <p className="text-[15px] text-gray-800 font-medium mb-4">
              Most backtests look good. Few survive validation.
            </p>
            <h2 className="text-xl md:text-[24px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
              Why structured validation matters
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.7] max-w-[560px] mx-auto">
              Most strategy backtests produce misleading results because they
              conflate exploration with validation. Quanthop separates these
              stages explicitly.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                title: 'Without structured validation',
                items: [
                  'Optimized to a single asset and timeframe',
                  'No out-of-sample testing',
                  'Results collapse under realistic costs',
                  'No monitoring for post-deployment decay',
                  'False confidence in performance',
                ],
                accent: '#ef4444',
                bg: 'bg-red-50',
                border: 'border-red-200/80',
              },
              {
                title: 'With Quanthop validation pipeline',
                items: [
                  'Multi-asset baseline before optimization',
                  'Parameter stability scoring across search space',
                  'Walk-forward out-of-sample testing',
                  'Continuous adaptive flow validation',
                  'Degradation detection with drift alerts',
                ],
                accent: '#9d4edd',
                bg: 'bg-purple-50/50',
                border: 'border-purple-200/80',
              },
            ].map((col) => (
              <div key={col.title} className={`rounded-lg border ${col.border} ${col.bg} px-6 py-6`}>
                <h3 className="text-[14px] font-semibold text-gray-950 mb-4">{col.title}</h3>
                <div className="space-y-2.5">
                  {col.items.map((item) => (
                    <div key={item} className="flex items-start gap-2.5">
                      <span className="w-1.5 h-1.5 rounded-full mt-[6px] flex-shrink-0" style={{ background: col.accent }} />
                      <span className="text-[13px] text-gray-600 leading-snug">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════ 9. CTA ════════════════ */}
      <section className="relative py-28 px-4 sm:px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-[#9d4edd]/[0.03] to-transparent pointer-events-none" />
        <div className="max-w-[600px] mx-auto text-center relative z-10">
          <h2 className="text-xl md:text-[24px] font-bold text-white tracking-[-0.02em] mb-5">
            Build strategies you can trust
          </h2>
          <p className="text-[15px] text-gray-300/70 leading-[1.7] mb-3">
            Limited research seats available. Start building with a
            validation-first workflow.
          </p>
          <p className="text-[13px] text-gray-400/60 leading-[1.7] mb-10">
            Five-stage pipeline. Pass/fail gates at every stage. No shortcuts.
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#9d4edd] hover:bg-[#8b3ec7] text-white rounded text-sm font-medium transition-colors"
          >
            Start Research
            <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
          </Link>
          <div className="mt-4">
            <a href="#pipeline-overview" className="text-[13px] text-gray-400 hover:text-gray-200 transition-colors">
              View the validation pipeline overview <span className="text-gray-600">&rarr;</span>
            </a>
          </div>
        </div>
      </section>

      {/* ════════════════ NAV FOOTER ════════════════ */}
      <section className="py-12 px-4 sm:px-6 lg:px-10 border-t border-gray-700/40">
        <div className="max-w-[960px] mx-auto flex items-center justify-between">
          <Link to="/features/workspace" className="inline-flex items-center gap-2 text-[13px] text-gray-500 hover:text-gray-300 transition-colors">
            <FontAwesomeIcon icon={faArrowLeft} className="text-[10px]" />
            Research Workspace
          </Link>
          <Link to="/features/optimization" className="inline-flex items-center gap-2 text-[13px] text-[#9d4edd] hover:text-[#b266ff] transition-colors font-medium">
            Next: Parameter Optimization
            <FontAwesomeIcon icon={faArrowRight} className="text-[11px]" />
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default ValidationPage;
